import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { hasUnreadRestaurantChat } from "../utils/chatNotifications";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import styled, { css, keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faClipboard, faComments, faReceipt, faFileArrowDown } from "@fortawesome/free-solid-svg-icons";
import { Row, Col } from "react-bootstrap";
import { fetchAllReservations } from "../actions/restaurantActions";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import CollapsibleChat from "./chat/CollapsibleChat";
import ReservationCard from "./ReservationCard";
import { api } from "../App";
const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;
const slideOut = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(100%); }
`;
const BarContainer = styled.div`
  width: 100vw;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  box-shadow: 4px 0 10px rgba(0, 0, 0, 0.2);

  animation: ${({
  $clicked
}) => $clicked ? slideIn : slideOut} 0.5s forwards;
`;
const InsideContainerBox = styled.div`
  width: 100%;
  height: 100%;
  background-color: #ee754c;
  overflow: hidden;
  display: flex;
  justify-content: end;
`;
const InsideContainer = styled.div`
  padding: 30px 5px 5px 10px;
  position: relative;
  width: 98%;
  background-color: white;
  height: 100%;
  overflow: hidden;

  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: #e20202ff;
    &:hover {
      background-color: #bbbbbb;
    }
  }

  &::-webkit-scrollbar {
    width: 12px;
    background-color: transparent;
  }

  @media (max-width: 768px) {
    padding-top: 10px;
    padding-left: 15px;
    padding-right: 15px;
  }

  @media (max-width: 480px) {
    padding-top: 50px;
  }
`;
const orbit = (start, end) => keyframes`
  from { transform: rotate(${start}deg); }
  to { transform: rotate(${end}deg); }
`;
const CircleContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  position: fixed;
  top: 15px;
  right: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #98afad;
  cursor: pointer;
  padding: 2px;
  z-index: 1001;

  @media (max-width: 768px) {
    /* Align with the 36px menu control in the app's 56px mobile header. */
    top: 10px;
    right: 54px;
    bottom: auto;
    width: 36px;
    height: 36px;
    box-shadow: none;

    > svg {
      font-size: 24px !important;
    }
  }
`;
const NotificationCircle = styled.div`
  width: 25px;
  height: 25px;
  border-radius: 50%;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;

  bottom: 20px;
  right: 20px;

  & > div {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  ${({
  $animationDegrees,
  $animate
}) => $animate && css`
      transform-origin: 29px 27px;
      animation: ${orbit($animationDegrees?.start ?? 0, $animationDegrees?.end ?? 0)}
        0.5s forwards;
    `}

  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
    bottom: -3px;
    right: -4px;
    font-size: 9px;
    font-weight: 700;
    line-height: 1;
    animation: none;
    transform: none;

    > div {
      transform: none !important;
    }
  }
`;
const OrangeNotification = styled(NotificationCircle)`
  color: white;
  background: #ee754c;

  @media (max-width: 768px) {
    left: -4px;
    right: auto;
  }
`;
const RedCircle = styled(NotificationCircle)`
  color: white;
  background: red;
`;
const tremble = keyframes`
  0% { transform: rotate(0deg); }
  25% { transform: rotate(-6deg); }
  75% { transform: rotate(6deg); }
  100% { transform: rotate(0deg); }
`;
const AnimatedIcon = styled(FontAwesomeIcon)`
  color: #5fddb3;
  font-size: 60px;

  ${({
  $animate
}) => $animate && css`
      animation: ${tremble} 0.2s ease-in-out 3;
    `}
`;
const NotificationDot = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 10px;
  height: 10px;
  background-color: #ee754c;
  border-radius: 50%;
  border: 1px solid white;
`;

const BillingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 8px 20px;
`;

const BillingCard = styled.div`
  border: 1px solid rgba(153, 176, 173, 0.45);
  border-radius: 8px;
  background: #fff;
  padding: 12px;
  font-family: "Montserrat", "Segoe UI", sans-serif;
`;

const BillingCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 9px;
`;

const BillingName = styled.div`
  min-width: 0;
  font-weight: 700;
  color: #4b3335;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BillingAmount = styled.div`
  flex: 0 0 auto;
  font-weight: 800;
  color: #ee754c;
`;

const BillingMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px 12px;
  font-size: 12px;
  color: #6f7f7c;
  margin-bottom: 9px;
`;

const BillingStatusRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 9px;
`;

const BillingStatus = styled.span`
  border-radius: 999px;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $pending }) => ($pending ? "#fff3df" : "#edf5f3")};
  color: ${({ $pending }) => ($pending ? "#c97a00" : "#55736f")};
`;

const BillingActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
`;

const BillingButton = styled.button`
  border: 0;
  border-radius: 6px;
  padding: 7px 9px;
  background: ${({ $secondary }) => ($secondary ? "#99b0ad" : "#ee754c")};
  color: white;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const BillingDocumentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 8px 0;
  color: #62726f;
  font-size: 11px;
`;

const BillingFileLink = styled.button`
  width: 100%;
  min-width: 0;
  border: 0;
  border-radius: 7px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f6f9f8;
  color: #55736f;
  font: inherit;
  font-weight: 600;
  text-align: left;
  cursor: pointer;

  svg {
    flex: 0 0 auto;
    color: #99b0ad;
    font-size: 13px;
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: #edf4f2;
    color: #405f5b;
  }
`;

const BillingErrorDetail = styled.div`
  margin: 6px 0;
  padding: 7px 8px;
  border-radius: 6px;
  background: #fff0ee;
  color: #a13b2f;
  font-size: 11px;
  line-height: 1.35;
  word-break: break-word;
`;

const BillingUploadLabel = styled.label`
  display: inline-flex;
  align-items: center;
  border: 1px solid #99b0ad;
  border-radius: 6px;
  padding: 6px 8px;
  background: #fff;
  color: #55736f;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;

  input { display: none; }
`;

const BillingStateText = styled.div`
  padding: 25px 16px;
  text-align: center;
  color: #6f7f7c;
  font-family: "Montserrat", "Segoe UI", sans-serif;
  font-size: 13px;
`;

const getBooleanValue = (value, fallback = true) => {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true") return true;
  if (value === 0 || value === "0" || value === "false") return false;
  return fallback;
};
function NotificationBell({
  restaurant_id,
  allReservations,
  reservationsWithChatMessages,
  reservations_past_estimated_time
}) {
  const {
    t
  } = useTranslation();
  const [activeKey, setActiveKey] = useState("reservas");
  const userInfoState = useSelector(state => state.userLogin);
  const {
    userInfo
  } = userInfoState;
  const restaurantDetailsState = useSelector(state => state.restaurantDetails);
  const restaurantSettings = restaurantDetailsState?.restaurants;
  const showTableNotFreedNotification = useMemo(() => {
    return getBooleanValue(restaurantSettings?.show_table_not_freed_notification ?? restaurantSettings?.restaurant?.show_table_not_freed_notification ?? allReservations?.[0]?.restaurant?.show_table_not_freed_notification ?? allReservations?.[0]?.show_table_not_freed_notification ?? reservations_past_estimated_time?.[0]?.restaurant?.show_table_not_freed_notification ?? reservations_past_estimated_time?.[0]?.show_table_not_freed_notification, true);
  }, [restaurantSettings, allReservations, reservations_past_estimated_time]);
  const notificationBellRef = useRef(null);
  const colRef = useRef(null);
  const contentRef = useRef(null);
  const inactivityTimeoutRef = useRef(null);
  const dispatch = useDispatch();
  const [clicked, setClicked] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [pendingNumber, setPendingNumber] = useState(0);
  const [cancelledNumber, setCancelledNumber] = useState(0);
  const [allReservationsWithDateSeparators, setAllReservationsWithDateSeparators] = useState([]);
  const [hasUnreadChatNotifications, setHasUnreadChatNotifications] = useState(false);
  const [hasNewReservation, setHasNewReservation] = useState(false);
  const [billingItems, setBillingItems] = useState([]);
  const [billingPendingCount, setBillingPendingCount] = useState(0);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [billingUpdatingId, setBillingUpdatingId] = useState(null);
  const [billingConnection, setBillingConnection] = useState(null);
  const billingRestaurantRef = useRef(restaurant_id);
  billingRestaurantRef.current = restaurant_id;
  const isStripeConnected = Boolean(
    billingConnection?.connected &&
    String(billingConnection.restaurantId) === String(restaurant_id)
  );
  const visibleBillingPendingCount = isStripeConnected ? billingPendingCount : 0;

  useEffect(() => {
    if (!isStripeConnected && activeKey === "faturacao") setActiveKey("reservas");
  }, [isStripeConnected, activeKey]);

  const loadPrepaymentBilling = useCallback(async ({ silent = false } = {}) => {
    if (!restaurant_id || !userInfo?.token) {
      setBillingItems([]);
      setBillingPendingCount(0);
      setBillingConnection(null);
      return;
    }

    if (!silent) setBillingLoading(true);
    setBillingError("");

    try {
      const response = await api.get(
        `/reservations/prepayment-billing/${restaurant_id}/`,
        { headers: { Authorization: `Bearer ${userInfo.token}` } },
      );

      if (String(billingRestaurantRef.current) !== String(restaurant_id)) return;
      setBillingConnection({
        restaurantId: restaurant_id,
        connected: response.data?.stripe_connected === true,
      });
      setBillingItems(Array.isArray(response.data?.items) ? response.data.items : []);
      setBillingPendingCount(Number(response.data?.pending_count || 0));
    } catch (error) {
      if (String(billingRestaurantRef.current) !== String(restaurant_id)) return;
      console.error("Failed to load prepayment billing notifications:", error);
      setBillingError(t("ui.screens.notifications.billingLoadError"));
    } finally {
      if (!silent) setBillingLoading(false);
    }
  }, [restaurant_id, userInfo?.token, t]);

  const updateBillingStatus = useCallback(async (reservationId, action) => {
    if (!userInfo?.token) return;

    setBillingUpdatingId(reservationId);
    setBillingError("");

    try {
      await api.post(
        `/reservations/prepayment-billing/reservation/${reservationId}/`,
        { action },
        { headers: { Authorization: `Bearer ${userInfo.token}` } },
      );
      await loadPrepaymentBilling({ silent: true });
      window.dispatchEvent(new Event("prepayment_billing_updated"));
    } catch (error) {
      console.error("Failed to update prepayment billing status:", error);
      setBillingError(t("ui.screens.notifications.billingUpdateError"));
    } finally {
      setBillingUpdatingId(null);
    }
  }, [userInfo?.token, loadPrepaymentBilling, t]);

  const uploadBillingAttachment = useCallback(async (reservationId, documentType, file) => {
    if (!userInfo?.token || !file) return;
    setBillingUpdatingId(reservationId);
    setBillingError("");
    const formData = new FormData();
    formData.append("document_type", documentType);
    formData.append("file", file);
    try {
      await api.post(
        `/reservations/prepayment-billing/reservation/${reservationId}/attachment/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      await loadPrepaymentBilling({ silent: true });
      window.dispatchEvent(new Event("prepayment_billing_updated"));
    } catch (error) {
      console.error("Failed to upload fiscal document:", error);
      setBillingError(
        error?.response?.data?.detail || t("ui.screens.notifications.billingUploadError"),
      );
    } finally {
      setBillingUpdatingId(null);
    }
  }, [userInfo?.token, loadPrepaymentBilling, t]);

  const downloadBillingAttachment = useCallback(async (attachment) => {
    if (!userInfo?.token || !attachment?.download_url) return;
    setBillingError("");
    try {
      const response = await api.get(attachment.download_url, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
        responseType: "blob",
      });
      const objectUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = attachment.name || "document";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Failed to download fiscal document:", error);
      setBillingError(t("ui.screens.notifications.billingDownloadError"));
    }
  }, [userInfo?.token, t]);

  const getFiscalStatusLabel = useCallback((status, type = "invoice") => {
    if (status === "failed") return t("ui.screens.notifications.fiscalFailed");
    if (status === "handled") {
      return t(type === "credit_note" ? "ui.screens.notifications.creditNoteHandled" : "ui.screens.notifications.invoicingHandled");
    }
    if (status === "pending") {
      return t(type === "credit_note" ? "ui.screens.notifications.creditNotePending" : "ui.screens.notifications.invoicingPending");
    }
    return t("ui.screens.notifications.notRequired");
  }, [t]);

  const formatBillingMoney = useCallback((amountCents, currency = "EUR") => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: String(currency || "EUR").toUpperCase(),
      }).format(Number(amountCents || 0) / 100);
    } catch (error) {
      return `${(Number(amountCents || 0) / 100).toFixed(2)} €`;
    }
  }, []);

  const getPaymentStatusLabel = useCallback((status) => {
    const keyByStatus = {
      paid: "paymentPaid",
      retained: "paymentRetained",
      refund_pending: "paymentRefundPending",
      refunded: "paymentRefunded",
    };
    return t(`ui.screens.notifications.${keyByStatus[status] || "paymentPaid"}`);
  }, [t]);

  useEffect(() => {
    loadPrepaymentBilling({ silent: true });

    const refreshBilling = () => loadPrepaymentBilling({ silent: true });
    window.addEventListener("prepayment_billing_updated", refreshBilling);
    const intervalId = window.setInterval(refreshBilling, 60000);

    return () => {
      window.removeEventListener("prepayment_billing_updated", refreshBilling);
      window.clearInterval(intervalId);
    };
  }, [loadPrepaymentBilling]);

  useEffect(() => {
    if (clicked) loadPrepaymentBilling({ silent: true });
  }, [clicked, loadPrepaymentBilling]);

  useEffect(() => {
    if (allReservations) {
      const hasUnread = allReservations.some(reservation => hasUnreadRestaurantChat(reservation, userInfo));
      setHasUnreadChatNotifications(hasUnread);
      const hasFreeTableWarning = showTableNotFreedNotification && reservations_past_estimated_time?.some(reservation => String(reservation.status).toLowerCase() === "attended");
      const hasNew = allReservations.some(reservation => reservation.status === "pending_restaurant" || reservation.status === "edited" || reservation.status === "Waiting_List" || reservation.status === "waiting_list" || reservation.cancelled_warning === true) || hasFreeTableWarning;
      setHasNewReservation(hasNew);
    } else {
      const hasFreeTableWarning = showTableNotFreedNotification && reservations_past_estimated_time?.some(reservation => String(reservation.status).toLowerCase() === "attended");
      setHasNewReservation(Boolean(hasFreeTableWarning));
      setHasUnreadChatNotifications(false);
    }
  }, [allReservations, userInfo, reservations_past_estimated_time, showTableNotFreedNotification]);
  const handleScrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  };
  const handleTabSelect = key => {
    setActiveKey(key);
    handleScrollToTop();
  };
  const handleChatToggle = () => {};
  const stableRestaurantId = useMemo(() => restaurant_id, [restaurant_id]);
  useEffect(() => {
    if (stableRestaurantId) {
      dispatch(fetchAllReservations(stableRestaurantId, null, null, null, "all_pending_reservations_from_all_time"));
    }
  }, [dispatch, stableRestaurantId]);
  const PENDING_STATUSES = new Set(["pending_restaurant", "edited"]);
  const [freeTablesReservations, setFreeTablesReservations] = useState([]);
  const [pendingReservations, setPendingReservations] = useState([]);
  useEffect(() => {
    if (showTableNotFreedNotification) {
      setFreeTablesReservations(reservations_past_estimated_time?.filter(r => String(r.status).toLowerCase() === "attended") ?? []);
    } else {
      setFreeTablesReservations([]);
    }
    setPendingReservations(allReservations?.filter(r => PENDING_STATUSES.has(r.status) || !!r.cancelled_warning) ?? []);
  }, [reservations_past_estimated_time, allReservations, showTableNotFreedNotification]);
  useEffect(() => {
    if (!allReservations?.length) {
      const freeTablesCount = showTableNotFreedNotification ? reservations_past_estimated_time?.filter(r => String(r.status).toLowerCase() === "attended").length ?? 0 : 0;
      setPendingNumber(freeTablesCount + visibleBillingPendingCount);
      return;
    }
    const pendingBaseCount = allReservations.filter(r => r.cancelled_warning === true || r.status === "Cancelled_By_Customer_Warning" || r.status === "pending_restaurant" || r.status === "new_chat_message" || r.status === "edited" || r.status === "Waiting_List" || r.status === "waiting_list" || r.chat_notification_restaurant_all).length;
    const freeTablesCount = showTableNotFreedNotification ? reservations_past_estimated_time?.filter(r => String(r.status).toLowerCase() === "attended").length ?? 0 : 0;
    setPendingNumber(pendingBaseCount + freeTablesCount + visibleBillingPendingCount);
  }, [allReservations, reservations_past_estimated_time, showTableNotFreedNotification, visibleBillingPendingCount]);
  useEffect(() => {
    if (allReservations && allReservations.length > 0) {
      const sortedReservations = [...allReservations].sort((a, b) => a.date.localeCompare(b.date));
      const reservationsWithSeparators = [];
      let lastDate = null;
      sortedReservations.forEach(reservation => {
        if (reservation.date !== lastDate) {
          reservationsWithSeparators.push({
            isDateSeparator: true,
            date: reservation.date
          });
          lastDate = reservation.date;
        }
        reservationsWithSeparators.push(reservation);
      });
      setAllReservationsWithDateSeparators(reservationsWithSeparators);
    }
  }, [allReservations]);
  const animationDegreesYellow = clicked ? {
    start: 180,
    end: 0
  } : {
    start: 0,
    end: 180
  };
  const animationDegreesRed = clicked ? {
    start: 140,
    end: -40
  } : {
    start: -40,
    end: 140
  };
  const counterRotate = clicked ? "rotate(0deg)" : "rotate(-180deg)";
  const counterRotateRed = clicked ? "rotate(40deg)" : "rotate(-140deg)";
  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  }, []);
  const resetInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    inactivityTimeoutRef.current = setTimeout(() => {
      setClicked(false);
    }, 3 * 60 * 1000);
  }, [clearInactivityTimer]);
  const hasOtherModalOpen = useCallback(() => {
    return !!document.querySelector(".modal.show");
  }, []);
  useEffect(() => {
    if (!clicked) {
      clearInactivityTimer();
      return;
    }
    resetInactivityTimer();
    const activityEvents = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    const handleUserActivity = () => {
      resetInactivityTimer();
    };
    activityEvents.forEach(eventName => {
      window.addEventListener(eventName, handleUserActivity, {
        passive: true
      });
    });
    return () => {
      activityEvents.forEach(eventName => {
        window.removeEventListener(eventName, handleUserActivity);
      });
      clearInactivityTimer();
    };
  }, [clicked, resetInactivityTimer, clearInactivityTimer]);
  useEffect(() => {
    const handleClickOutside = event => {
      if (!clicked) return;
      if (notificationBellRef.current && notificationBellRef.current.contains(event.target)) {
        return;
      }
      if (colRef.current && colRef.current.contains(event.target)) {
        return;
      }
      if (hasOtherModalOpen()) {
        return;
      }
      setClicked(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [clicked, hasOtherModalOpen]);
  const handleClick = () => {
    setAnimate(true);
    setClicked(prev => !prev);
    setTimeout(() => {
      setAnimate(false);
    }, 200);
  };
  return <>
      <CircleContainer ref={notificationBellRef} onClick={handleClick}>
        <AnimatedIcon $animate={animate} icon={faBell} style={{
        color: "#ffffff",
        fontSize: "30px"
      }} />

        {pendingNumber > 0 && <OrangeNotification $animate={true} $animationDegrees={animationDegreesYellow}>
            <div style={{
          transform: counterRotate
        }}>
              {pendingNumber < 10 ? pendingNumber : "9+"}
            </div>
          </OrangeNotification>}

        {cancelledNumber > 0 && <RedCircle $animate={true} $animationDegrees={animationDegreesRed}>
            <div style={{
          transform: counterRotateRed
        }}>
              {cancelledNumber < 10 ? cancelledNumber : "9+"}
            </div>
          </RedCircle>}
      </CircleContainer>

      <BarContainer $clicked={clicked}>
        <Row style={{
        height: "100%",
        display: "flex",
        justifyContent: "end",
        padding: 0,
        margin: "0"
      }}>
          <Col ref={colRef} xs={12} sm={12} md={6} xl={4} style={{
          color: "black",
          height: "100%",
          padding: 0,
          margin: "0"
        }}>
            <InsideContainerBox>
              <div className="menu-left"></div>
              <InsideContainer>
                <div style={{
                position: "relative",
                height: "100%"
              }}>
                  <Tabs activeKey={activeKey} onSelect={handleTabSelect} defaultActiveKey="reservas" id="uncontrolled-tab-example" style={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "white",
                  zIndex: 1000
                }}>
                    <Tab eventKey="reservas" title={<>
                          <b style={{
                      color: "inherit"
                    }}>{t("ui.components.notificationBell.reservas")}</b>
                          <FontAwesomeIcon icon={faClipboard} style={{
                      marginLeft: "5px",
                      color: "inherit"
                    }} />
                          {hasNewReservation && <NotificationDot />}
                        </>} />
                    <Tab eventKey="mensagens" title={<>
                          <b style={{
                      color: "inherit"
                    }}>{t("ui.components.notificationBell.mensagens")}</b>
                          <FontAwesomeIcon icon={faComments} style={{
                      marginLeft: "5px",
                      color: "inherit"
                    }} />
                          {hasUnreadChatNotifications && <NotificationDot />}
                        </>} />
                    {isStripeConnected && <Tab eventKey="faturacao" title={<>
                          <b style={{
                      color: "inherit"
                    }}>{t("ui.components.notificationBell.faturacao")}</b>
                          <FontAwesomeIcon icon={faReceipt} style={{
                      marginLeft: "5px",
                      color: "inherit"
                    }} />
                          {billingPendingCount > 0 && <NotificationDot />}
                        </>} />}
                  </Tabs>

                  <div ref={contentRef} style={{
                  position: "absolute",
                  top: "65px",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  width: "100%",
                  overflowY: "auto",
                  overflowBehavior: "contain"
                }}>
                    {activeKey === "reservas" ? <div style={{
                    overflowBehavior: "contain",
                    width: "100%",
                    display: "grid",
                    gap: "6px"
                  }}>
                        {pendingReservations.length > 0 && <ReservationCard style={{
                      width: "100%"
                    }} reservations={pendingReservations} isNotifications={true} />}

                        {showTableNotFreedNotification && freeTablesReservations.length > 0 && <ReservationCard style={{
                      width: "100%"
                    }} reservations={freeTablesReservations} past_estimated_time={true} isNotifications={true} />}
                      </div> : activeKey === "mensagens" ? <div style={{
                    padding: "15px",
                    overflowBehavior: "contain"
                  }}>
                        {allReservations && allReservations.filter(reservation => hasUnreadRestaurantChat(reservation, userInfo)).map(reservation => <CollapsibleChat key={reservation.id} reservation={reservation} isClient={false} style={{
                      width: "100%"
                    }} isNewMessage={true} onToggle={handleChatToggle} />)}

                        {reservationsWithChatMessages && reservationsWithChatMessages.filter(reservation => reservation.chat_has_messages && !hasUnreadRestaurantChat(reservation, userInfo)).map(reservation => <CollapsibleChat key={reservation.id} reservation={reservation} isClient={false} style={{
                      width: "100%"
                    }} isNewMessage={false} onToggle={handleChatToggle} />)}
                      </div> : isStripeConnected ? <BillingList>
                        {billingError && <BillingStateText>{billingError}</BillingStateText>}
                        {billingLoading && billingItems.length === 0 ? <BillingStateText>
                            {t("ui.screens.notifications.loadingPayments")}
                          </BillingStateText> : billingItems.length === 0 ? <BillingStateText>
                            {t("ui.screens.notifications.noPrepaymentActivity")}
                          </BillingStateText> : billingItems.map(item => {
                        const invoiceAttention = ["pending", "failed"].includes(item.invoice_status);
                        const creditNoteAttention = ["pending", "failed"].includes(item.credit_note_status);
                        const isUpdating = billingUpdatingId === item.reservation_id;
                        const invoiceAttachment = item.attachments?.invoice;
                        const isMoloni = item.invoicing_mode === "moloni";
                        return <BillingCard key={item.reservation_id}>
                              <BillingCardHeader>
                                <BillingName>{item.full_name}</BillingName>
                                <BillingAmount>{formatBillingMoney(item.amount_cents, item.currency)}</BillingAmount>
                              </BillingCardHeader>

                              <BillingMeta>
                                <span>{item.date} {String(item.time || "").slice(0, 5)}</span>
                                <span>{item.number_of_people} {t("ui.screens.notifications.people")}</span>
                              </BillingMeta>

                              <BillingStatusRow>
                                <BillingStatus>
                                  {t("ui.screens.notifications.paymentStatus")}: {getPaymentStatusLabel(item.payment_status)}
                                </BillingStatus>
                                <BillingStatus $pending={invoiceAttention}>
                                  {t("ui.screens.notifications.invoicingStatus")}: {getFiscalStatusLabel(item.invoice_status, "invoice")}
                                </BillingStatus>
                                {item.credit_note_status && item.credit_note_status !== "not_required" && <BillingStatus $pending={creditNoteAttention}>
                                    {t("ui.screens.notifications.creditNoteStatus")}: {getFiscalStatusLabel(item.credit_note_status, "credit_note")}
                                  </BillingStatus>}
                              </BillingStatusRow>

                              {(item.moloni_invoice_document_number || invoiceAttachment) && <BillingDocumentInfo>
                                  {item.moloni_invoice_document_number && <span>
                                    {t("ui.screens.notifications.moloniInvoice")}: {item.moloni_invoice_document_number}
                                  </span>}
                                  {invoiceAttachment && <BillingFileLink
                                    type="button"
                                    disabled={isUpdating}
                                    title={invoiceAttachment.name}
                                    onClick={() => downloadBillingAttachment(invoiceAttachment)}
                                  >
                                    <FontAwesomeIcon icon={faFileArrowDown} />
                                    <span>{invoiceAttachment.name}</span>
                                  </BillingFileLink>}
                                </BillingDocumentInfo>}
                              {item.moloni_invoice_error && item.invoice_status === "failed" && <BillingErrorDetail>
                                  {item.moloni_invoice_error}
                                </BillingErrorDetail>}

                              <BillingActions>
                                {isMoloni && item.invoice_status === "failed" && <BillingButton
                                  type="button"
                                  disabled={isUpdating}
                                  onClick={() => updateBillingStatus(item.reservation_id, "retry_auto_invoice")}
                                >
                                  {t("ui.screens.notifications.retryMoloniInvoice")}
                                </BillingButton>}

                                <BillingButton
                                  type="button"
                                  disabled={isUpdating}
                                  $secondary={!invoiceAttention}
                                  onClick={() => updateBillingStatus(item.reservation_id, invoiceAttention ? "invoice_handled" : "invoice_pending")}
                                >
                                  {t(`ui.screens.notifications.${invoiceAttention ? "markInvoicingHandled" : "reopenInvoicing"}`)}
                                </BillingButton>

                                <BillingUploadLabel>
                                  {invoiceAttachment
                                    ? t("ui.screens.notifications.replaceInvoiceFile")
                                    : t("ui.screens.notifications.attachInvoiceFile")}
                                  <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                                    disabled={isUpdating}
                                    onChange={(event) => {
                                      const file = event.target.files?.[0];
                                      if (file) uploadBillingAttachment(item.reservation_id, "invoice", file);
                                      event.target.value = "";
                                    }}
                                  />
                                </BillingUploadLabel>

                              </BillingActions>

                              {item.credit_note_status && item.credit_note_status !== "not_required" && <>
                                {item.moloni_credit_note_document_number && <BillingDocumentInfo>
                                    <span>{t("ui.screens.notifications.moloniCreditNote")}: {item.moloni_credit_note_document_number}</span>
                                  </BillingDocumentInfo>}
                                {item.moloni_credit_note_error && item.credit_note_status === "failed" && <BillingErrorDetail>
                                    {item.moloni_credit_note_error}
                                  </BillingErrorDetail>}
                                <BillingActions style={{ marginTop: "7px" }}>
                                  {isMoloni && item.credit_note_status === "failed" && <BillingButton
                                    type="button"
                                    disabled={isUpdating}
                                    onClick={() => updateBillingStatus(item.reservation_id, "retry_auto_credit_note")}
                                  >
                                    {t("ui.screens.notifications.retryMoloniCreditNote")}
                                  </BillingButton>}
                                  <BillingButton
                                    type="button"
                                    disabled={isUpdating}
                                    $secondary={!creditNoteAttention}
                                    onClick={() => updateBillingStatus(item.reservation_id, creditNoteAttention ? "credit_note_handled" : "credit_note_pending")}
                                  >
                                    {t(`ui.screens.notifications.${creditNoteAttention ? "markCreditNoteHandled" : "reopenCreditNote"}`)}
                                  </BillingButton>
                                </BillingActions>
                              </>}
                            </BillingCard>;
                      })}
                      </BillingList> : null}
                  </div>
                </div>
              </InsideContainer>
            </InsideContainerBox>
          </Col>
        </Row>
      </BarContainer>
    </>;
}
export default NotificationBell;
